// Client for the community forum (netlify/functions/forum-read.ts,
// forum-post.ts, forum-moderate.ts). Like the chat and activity log, this
// is best-effort public data stored in Netlify Blobs — never a source of
// truth for anything on-chain, and nothing sensitive should go through it.
//
// Note on identity: `author`/`isWallet` are client-supplied. A connected
// wallet posts under its WAX account name, but the server does not verify
// a signature, so treat a wallet badge in the forum as a convention, not
// proof. Same trust level as the rest of this site today.

export interface ForumCategory {
  id: string;
  name: string;
  description: string;
}

export interface ForumPost {
  id: string;
  parentId: string | null;
  author: string;
  isWallet: boolean;
  body: string;
  createdAt: string;
  editedAt?: string;
  editedBy?: string;
  deleted?: boolean;
  deletedBy?: string;
  /** Admin-only; absent for everyone else. */
  ipHash?: string;
}

export interface ForumThreadSummary {
  id: string;
  categoryId: string;
  title: string;
  author: string;
  isWallet: boolean;
  createdAt: string;
  lastActivityAt: string;
  lastAuthor: string;
  replyCount: number;
  pinned: boolean;
  locked: boolean;
  hidden: boolean;
  views: number;
}

export interface ForumThreadDoc {
  id: string;
  categoryId: string;
  title: string;
  createdAt: string;
  posts: ForumPost[];
}

export interface ForumReport {
  id: string;
  threadId: string;
  postId: string;
  reason: string;
  reporter: string;
  createdAt: string;
  resolved: boolean;
  resolvedBy?: string;
}

export interface ForumBans {
  names: string[];
  ipHashes: string[];
  words: string[];
}

export interface ForumIndexResponse {
  threads: ForumThreadSummary[];
  categories: ForumCategory[];
  counts: Record<string, number>;
  totals: { threads: number; posts: number };
  forumLocked: boolean;
  requireWallet: boolean;
}

export interface ForumThreadResponse {
  thread: ForumThreadDoc;
  summary: ForumThreadSummary | null;
  categories: ForumCategory[];
  forumLocked: boolean;
  requireWallet: boolean;
}

export interface ForumAdminResponse {
  threads: ForumThreadSummary[];
  bans: ForumBans;
  reports: ForumReport[];
  settings: { categories: ForumCategory[]; forumLocked: boolean; requireWallet: boolean };
}

/** Pulls the error message the functions send back ({ error }) so the UI
 *  can show "You're posting too fast" instead of a bare status code. */
async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    if (data && typeof data.error === "string") return data.error;
  } catch {
    /* non-JSON body — fall through */
  }
  return fallback;
}

export async function fetchForumIndex(categoryId?: string, account?: string): Promise<ForumIndexResponse> {
  const params = new URLSearchParams();
  if (categoryId && categoryId !== "all") params.set("category", categoryId);
  if (account) params.set("account", account);
  const res = await fetch(`/.netlify/functions/forum-read?${params.toString()}`);
  if (!res.ok) throw new Error(await readError(res, "Couldn't load the forum."));
  return res.json();
}

export async function fetchThread(id: string, account?: string, countView = false): Promise<ForumThreadResponse> {
  const params = new URLSearchParams({ view: "thread", id });
  if (account) params.set("account", account);
  if (countView) params.set("count", "1");
  const res = await fetch(`/.netlify/functions/forum-read?${params.toString()}`);
  if (!res.ok) throw new Error(await readError(res, "Couldn't load that thread."));
  return res.json();
}

export async function fetchForumAdmin(account: string): Promise<ForumAdminResponse> {
  const params = new URLSearchParams({ view: "admin", account });
  const res = await fetch(`/.netlify/functions/forum-read?${params.toString()}`);
  if (!res.ok) throw new Error(await readError(res, "Couldn't load moderation data."));
  return res.json();
}

async function post(body: Record<string, unknown>, endpoint: "forum-post" | "forum-moderate"): Promise<any> {
  const res = await fetch(`/.netlify/functions/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res, "That didn't go through — try again."));
  return res.json();
}

export function createThread(input: {
  categoryId: string;
  title: string;
  body: string;
  author: string;
  isWallet: boolean;
}): Promise<{ threadId: string }> {
  return post({ action: "thread", ...input }, "forum-post");
}

export function createReply(input: {
  threadId: string;
  parentId: string | null;
  body: string;
  author: string;
  isWallet: boolean;
}): Promise<{ postId: string }> {
  return post({ action: "reply", ...input }, "forum-post");
}

export function reportPost(input: {
  threadId: string;
  postId: string;
  reason: string;
  reporter: string;
}): Promise<{ ok: boolean }> {
  return post({ action: "report", ...input }, "forum-post");
}

/** Every admin action goes through one call — see forum-moderate.ts for
 *  the list and for what the account check is (and isn't). */
export function moderate(action: string, payload: Record<string, unknown>, account: string): Promise<any> {
  return post({ action, account, ...payload }, "forum-moderate");
}
